import { useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { Bell, Bug } from 'lucide-react';
import { useAuthStore } from '@/stores/auth';
import { CreateIssueModal } from '@/components/modals/CreateIssueModal';

const JOKES = [
  'Af hverju fór tölvan til læknis? Vegna þess að hún var með vírus! 🤒',
  'Hvað sagði húsið við annað húsið? Ég sé götu á milli okkar! 🏠',
  'Af hverju geta skeljar ekki deilt? Vegna þess að þær eru of selfish! 🐚',
  'Hvað sagði sjórinn við ströndina? Ekkert, hann bara veifaði! 🌊',
  'Af hverju fór fasteignasalinn á sjúkrahúsið? Hann var með húsónæmi! 🏥',
  'Hvað sagði lykillinn við lásinn? Þú opnar hjarta mitt! 🔑',
  'Af hverju geta skýin ekki haldið leyndarmáli? Vegna þess að þau bresta alltaf út! ☁️',
  'Hvað sagði veggurinn við loftið? Ég hitti þig í horninu! 📐',
];

function playBellSound() {
  const ctx = new AudioContext();
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.type = 'sine';
  osc.frequency.setValueAtTime(830, ctx.currentTime);
  osc.frequency.exponentialRampToValueAtTime(415, ctx.currentTime + 0.8);
  gain.gain.setValueAtTime(0.3, ctx.currentTime);
  gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + 0.8);
  osc.start(ctx.currentTime);
  osc.stop(ctx.currentTime + 0.8);
}

export default function Header() {
  const location = useLocation();
  const { user } = useAuthStore();
  const [showCreateIssue, setShowCreateIssue] = useState(false);
  const [joke, setJoke] = useState<string | null>(null);

  const handleBellClick = useCallback(() => {
    playBellSound();
    const randomJoke = JOKES[Math.floor(Math.random() * JOKES.length)];
    setJoke(randomJoke);
    setTimeout(() => setJoke(null), 4000);
  }, []);

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          {/* Mobile logo */}
          <div className="lg:hidden flex items-center gap-2">
            <div className="w-8 h-8 bg-prima-600 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-lg">P</span>
            </div>
            <span className="text-xl font-semibold text-gray-900">Prima</span>
          </div>

          {/* Desktop: Page title or search */}
          <div className="hidden lg:block">
            <h1 className="text-lg font-semibold text-gray-900">
              {getPageTitle(location.pathname)}
            </h1>
          </div>

          {/* Right side */}
          <div className="flex items-center gap-4">
            {/* Create GitHub Issue Button */}
            <button
              type="button"
              onClick={() => setShowCreateIssue(true)}
              className="hidden sm:flex items-center gap-2 px-3 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
              title="Búa til GitHub Issue"
            >
              <Bug className="h-4 w-4" />
              <span className="hidden md:inline">Issue</span>
            </button>

            <div className="relative">
              <button
                type="button"
                onClick={handleBellClick}
                className="relative rounded-full p-3 text-gray-400 hover:text-gray-500 hover:bg-gray-100 transition-transform active:scale-90"
              >
                <span className="sr-only">Tilkynningar</span>
                <Bell className={`h-5 w-5 ${joke ? 'animate-bounce' : ''}`} />
                <span className="absolute top-1 right-1 block h-2 w-2 rounded-full bg-red-500" />
              </button>
              {joke && (
                <div className="absolute right-0 top-12 w-72 bg-white rounded-lg shadow-lg border border-gray-200 p-4 z-50 animate-in fade-in slide-in-from-top-2">
                  <p className="text-sm text-gray-700">{joke}</p>
                </div>
              )}
            </div>

            {/* Mobile user avatar */}
            <div className="lg:hidden">
              {user?.picture ? (
                <img
                  src={user.picture}
                  alt={user.name}
                  className="w-8 h-8 rounded-full"
                />
              ) : (
                <div className="w-8 h-8 rounded-full bg-prima-100 flex items-center justify-center">
                  <span className="text-prima-700 font-medium text-sm">
                    {user?.name?.charAt(0) || 'U'}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Create Issue Modal */}
      <CreateIssueModal
        open={showCreateIssue}
        onOpenChange={setShowCreateIssue}
      />
    </header>
  );
}

function getPageTitle(pathname: string): string {
  if (pathname === '/') return 'Yfirlit';
  if (pathname.startsWith('/leads')) return 'Leads';
  if (pathname.startsWith('/calls')) return 'Símtöl';
  if (pathname.startsWith('/properties')) return 'Eignir';
  if (pathname.startsWith('/viewings')) return 'Skoðanir';
  if (pathname.startsWith('/agents')) return 'Söluaðilar';
  if (pathname.startsWith('/settings')) return 'Stillingar';
  return 'Prima';
}
